/**
 * PDF page raster helpers (pdf.js v3, CRA-friendly).
 */

let pdfjsLibPromise = null;

async function getPdfjs() {
  if (!pdfjsLibPromise) {
    pdfjsLibPromise = import("pdfjs-dist/build/pdf").then((pdfjsLib) => {
      if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
      }
      return pdfjsLib;
    });
  }
  return pdfjsLibPromise;
}

/**
 * Load a PDF File into a pdf.js document.
 * Caller should destroy when done.
 */
export async function loadPdfDocument(file) {
  if (!file) throw new Error("No PDF file");
  const pdfjsLib = await getPdfjs();
  const data = new Uint8Array(await file.arrayBuffer());
  const loadingTask = pdfjsLib.getDocument({ data });
  return loadingTask.promise;
}

/**
 * Render one page to a canvas (and optional data URL).
 * @returns {{ canvas: HTMLCanvasElement, dataUrl: string, width: number, height: number }}
 */
export async function renderPdfPage(pdf, pageNumber, { scale = 1.5, toDataUrl = true } = {}) {
  if (!pdf) throw new Error("No PDF document");
  const pageCount = pdf.numPages || 1;
  const n = Math.max(1, Math.min(pageCount, Math.floor(Number(pageNumber) || 1)));
  const page = await pdf.getPage(n);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");

  await page.render({ canvasContext: ctx, viewport }).promise;

  return {
    canvas,
    dataUrl: toDataUrl ? canvas.toDataURL("image/png") : null,
    width: canvas.width,
    height: canvas.height,
    pageNumber: n,
  };
}

/**
 * Render the first page of a PDF File to a PNG data URL (compat helper).
 */
export async function renderPdfFirstPage(file, { scale = 1.5 } = {}) {
  const pdf = await loadPdfDocument(file);
  try {
    const rendered = await renderPdfPage(pdf, 1, { scale, toDataUrl: true });
    return {
      previewUrl: rendered.dataUrl,
      pageCount: pdf.numPages || 1,
      width: rendered.width,
      height: rendered.height,
    };
  } finally {
    try {
      pdf.cleanup?.();
      await pdf.destroy?.();
    } catch {
      // ignore
    }
  }
}

export function isPdfFile(file) {
  if (!file) return false;
  const name = String(file.name || "").toLowerCase();
  return file.type === "application/pdf" || name.endsWith(".pdf");
}

export function isWordFile(file) {
  if (!file) return false;
  const name = String(file.name || "").toLowerCase();
  const type = String(file.type || "");
  return (
    name.endsWith(".docx") ||
    name.endsWith(".doc") ||
    type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    type === "application/msword"
  );
}

export { formatFileSize } from "../../../../utils/formatFileSize";

/** Soft cap so browser bake stays responsive. */
export const PDF_WATERMARK_MAX_PAGES = 40;
